<template>
  <!--
    Cookie Policy page. Lives on admin.kiey.com (and is also linked from
    the static kiey.com cookie banner). Lists what cookies Kiey uses and
    how to manage them.

    Audit summary (recorded May 3 2026):
    - kiey.com (static marketing): Google Analytics 4 via gtag.js
      (G-89EN50CRB6) on every page. NON-essential — gated behind the
      consent banner in cookie_consent.js.
    - admin.kiey.com (Vue app): no third-party trackers. Uses Firebase
      Auth session / IndexedDB (essential) and a localStorage flag for
      ToS acceptance versions.
  -->
  <div class="legal-page">
    <div class="legal-page-card">
      <div class="legal-page-header">
        <h1 class="legal-page-title">Cookie Policy</h1>
        <p class="legal-page-meta">Last updated May 3, 2026</p>
      </div>

      <div class="legal-page-body">
        <h2>What is a cookie?</h2>
        <p>
          Cookies and similar technologies (localStorage, IndexedDB,
          session storage, pixels, SDKs) are small pieces of data stored
          on your device when you visit a website. Some are essential
          for the site to function; others help us understand how the
          site is used.
        </p>

        <h2>Cookies and storage Kiey uses</h2>

        <h3>Essential (always on)</h3>
        <ul>
          <li><strong>Authentication session.</strong> Kiey relies on Firebase Authentication, which stores a session token in your browser's IndexedDB and localStorage. Without it, you cannot stay signed in.</li>
          <li><strong>Security &amp; CSRF.</strong> Short-lived cookies set by Firebase for sign-in flows, password reset, and session continuity.</li>
          <li><strong>Preferences.</strong> Your accepted Terms of Service version, route base, and similar non-tracking flags are kept in localStorage.</li>
        </ul>

        <h3>Analytics (consent-based, kiey.com only)</h3>
        <ul>
          <li>
            <strong>Google Analytics 4</strong> (gtag.js,
            measurement ID <code>G-89EN50CRB6</code>). Used on the
            kiey.com marketing site only. We use it to understand which
            pages and signup paths perform well. It is loaded only after
            you accept analytics on the cookie banner. Cookies set:
            <code>_ga</code>, <code>_ga_*</code> (1 year, third-party).
          </li>
        </ul>

        <h3>Marketing / advertising</h3>
        <p>None. Kiey does not use marketing or advertising cookies, retargeting pixels, or third-party ad networks.</p>

        <h2>Global Privacy Control</h2>
        <p>We honor browser-level Global Privacy Control (GPC) signals. If your browser sends a GPC signal, we treat it as an opt-out of "sale" or "sharing" under CCPA and we will not load analytics on the marketing site even if a stale "Accept" preference is in your browser.</p>

        <h2>Managing your preferences</h2>
        <p>
          On <a href="https://kiey.com">kiey.com</a> you can re-open the
          banner and change your choice at any time using the
          "<a href="https://kiey.com/cookie-preferences">Cookie Preferences</a>" link
          in the footer. Within
          <a href="https://admin.kiey.com">admin.kiey.com</a> only
          essential cookies are used, so there is nothing to opt out of.
        </p>
        <p>
          You can also block or delete cookies through your browser's
          settings. Doing so may break sign-in.
        </p>

        <h2>Questions</h2>
        <p>Email <a href="mailto:support@kiey.com">support@kiey.com</a> or submit a <a href="/legal/privacy-request">privacy request</a>.</p>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "CookiePolicy",
};
</script>

<style scoped>
.legal-page { min-height: 100vh; background: #f5f7fb; padding: 32px 16px; box-sizing: border-box; }
.legal-page-card { max-width: 920px; margin: 0 auto; background: #fff; border-radius: 12px; box-shadow: 0 4px 16px rgba(6, 29, 71, 0.06); padding: 36px 40px; box-sizing: border-box; }
.legal-page-header { border-bottom: 1px solid #e4e8f1; padding-bottom: 18px; margin-bottom: 24px; }
.legal-page-title { color: #061d47; font-size: 28px; margin: 0 0 6px; }
.legal-page-meta { color: #6a7388; font-size: 13px; margin: 0; }
.legal-page-body { color: #2d2d2d; line-height: 1.65; font-size: 15px; }
.legal-page-body h2 { color: #061d47; font-size: 20px; font-weight: 700; margin: 32px 0 12px; }
.legal-page-body h3 { color: #061d47; font-size: 16px; font-weight: 700; margin: 22px 0 10px; }
.legal-page-body p { margin: 0 0 12px; }
.legal-page-body ul { margin: 8px 0 16px; padding-left: 22px; }
.legal-page-body li { margin-bottom: 6px; }
.legal-page-body a { color: #29488d; text-decoration: none; }
.legal-page-body a:hover { text-decoration: underline; }
.legal-page-body code { background: #eef0f5; border-radius: 4px; padding: 1px 6px; font-size: 0.9em; font-family: "SF Mono", Menlo, Monaco, Consolas, monospace; }
@media (max-width: 720px) {
  .legal-page-card { padding: 24px 18px; }
  .legal-page-title { font-size: 22px; }
}
</style>
